// ImportRewardsModal — the file half of the Rewards tab, ported from
// .codelane/artifacts/t-43/rewards-tab-import-export.html: B (pick a file),
// C/D (the plan, in either mode) and E (a file this board can't take).
//
// The plan is the server's, not ours: POST /rewards/import/plan runs the same
// matching and the same validators the apply path runs, and writes nothing —
// which is why switching the mode simply asks again instead of recomputing
// anything here. A count the dialog worked out on its own could disagree with
// the transaction that follows, and the whole point of the screen is that it
// cannot.
//
// The two modes differ in exactly one thing: what happens to the rewards the
// file says nothing about. Merge leaves them (`kept`), Replace deletes them
// (`delete`) — so the mode is asked here, on top of the numbers it changes,
// rather than before the file is even read.

import { useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { File as FileIcon, Info, Upload } from "lucide-react";

import { Alert } from "../../../components/Alert/Alert";
import { Badge } from "../../../components/Badge/Badge";
import { Button } from "../../../components/Button/Button";
import { Modal } from "../../../components/Modal/Modal";
import { Skeleton } from "../../../components/Skeleton/Skeleton";
import { ApiError, describeError, errorProblems } from "../../../lib/errors";
import { importRewards, planRewardsImport } from "../api";
import { rewardPayloadText } from "../rewardPayload";
import {
  MAX_REWARDS_FILE_BYTES,
  REWARDS_FILE_TOO_BIG,
  fileRewardBasis,
  fileRewardKind,
  fileRewardRangeText,
  parseRewardsFile,
  readRewardsFileText,
  rewardsFileMeta,
} from "../rewardsFile";
import { isMemberContribution } from "../autoScored";
import type {
  Leaderboard,
  RewardsFile,
  RewardsFileEntry,
  RewardsImportMode,
  RewardsImportResult,
  RewardsPlan,
  Tier,
} from "../types";
import styles from "./ImportRewardsModal.module.css";

const MODE_LABEL: Record<RewardsImportMode, string> = {
  merge: "Merge",
  replace: "Replace",
};

const MODE_HINT: Record<RewardsImportMode, string> = {
  merge: "Rewards that are not in the file are left untouched.",
  replace: "The file becomes the whole reward table.",
};

// ──────────────────────────────────────────────────────────────────────────────
// Plan → rows
// ──────────────────────────────────────────────────────────────────────────────

type PlanRowKind = "create" | "update" | "delete" | "unchanged";

interface PlanRow {
  kind: PlanRowKind;
  /** The reward as it will be once the import runs — the board's row for a delete. */
  entry: RewardsFileEntry;
  /** Present on updates only: the row as it stands today. */
  before?: RewardsFileEntry;
}

const ROW_BADGE: Record<
  PlanRowKind,
  { label: string; variant: "success" | "info" | "danger" | "neutral" }
> = {
  create: { label: "Create", variant: "success" },
  update: { label: "Update", variant: "info" },
  delete: { label: "Delete", variant: "danger" },
  unchanged: { label: "Same", variant: "neutral" },
};

/**
 * The four outcomes as one list, ordered the way the reward table itself is:
 * by position. Grouping by outcome would scatter the rewards the operator
 * recognises across the screen, and position is what they will see afterwards.
 * Name breaks the ties a hand-edited file is free to produce.
 */
function planRows(plan: RewardsPlan): PlanRow[] {
  const rows: PlanRow[] = [
    ...plan.create.map((entry) => ({ kind: "create" as const, entry })),
    ...plan.update.map((u) => ({
      kind: "update" as const,
      entry: u.after,
      before: u.before,
    })),
    ...plan.delete.map((entry) => ({ kind: "delete" as const, entry })),
    ...plan.unchanged.map((entry) => ({ kind: "unchanged" as const, entry })),
  ];
  return rows.sort(
    (a, b) =>
      a.entry.position - b.entry.position ||
      a.entry.name.localeCompare(b.entry.name),
  );
}

/** Badges for the counts that are not zero, in the order the mockup lists them. */
function summaryBadges(plan: RewardsPlan) {
  return (
    [
      { n: plan.create.length, word: "create", variant: "success" as const },
      { n: plan.update.length, word: "update", variant: "info" as const },
      { n: plan.delete.length, word: "delete", variant: "danger" as const },
      {
        n: plan.unchanged.length,
        word: "unchanged",
        variant: "neutral" as const,
      },
      { n: plan.kept.length, word: "kept", variant: "neutral" as const },
    ] as const
  )
    .filter((b) => b.n > 0)
    .map((b) => ({ text: `${b.n} ${b.word}`, variant: b.variant }));
}

interface DiffItem {
  key: string;
  was: string;
  now: string;
}

/**
 * What an update actually rewrites. Only three fields can change — a reward's
 * range is immutable, so a file that moves one is a delete plus a create, and
 * the plan already says so.
 */
function diffItems(before: RewardsFileEntry, after: RewardsFileEntry): DiffItem[] {
  const items: DiffItem[] = [];
  if (before.name !== after.name) {
    items.push({ key: "name", was: before.name, now: after.name });
  }
  if (before.position !== after.position) {
    items.push({
      key: "position",
      was: String(before.position),
      now: String(after.position),
    });
  }
  const was = rewardPayloadText(before.payload);
  const now = rewardPayloadText(after.payload);
  if (was !== now && !sameJson(before.payload, after.payload)) {
    items.push({ key: "payload", was, now });
  }
  return items;
}

/**
 * Key order is not a change: the server compares the two payloads decoded
 * (jsonEqual), so a file that survived a round trip through some other tool
 * must not read as an edit here.
 */
function sameJson(a: unknown, b: unknown): boolean {
  return JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));
}

function canonical(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(canonical);
  if (v !== null && typeof v === "object") {
    const bag = v as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(bag).sort()) out[k] = canonical(bag[k]);
    return out;
  }
  return v;
}

const NUMBER_WORDS = [
  "zero",
  "one",
  "two",
  "three",
  "four",
  "five",
  "six",
  "seven",
  "eight",
  "nine",
  "ten",
  "eleven",
  "twelve",
];

/** Prose counts up to twelve — the footer note is read, not scanned. */
function numberWord(n: number): string {
  return NUMBER_WORDS[n] ?? String(n);
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many;
}

/**
 * The apply button's text. `changes` is null while the server recomputes for
 * the other mode: the counts on screen a moment ago belong to the mode the
 * operator just left, so the button says what it does without claiming any.
 */
function applyLabel(mode: RewardsImportMode, changes: number | null): string {
  if (changes === null) return mode === "merge" ? "Import changes" : "Replace";
  if (changes === 0) return "Nothing to change";
  const n = `${changes} ${plural(changes, "change", "changes")}`;
  return mode === "merge" ? `Import ${n}` : `Replace — ${n}`;
}

/** The footer's promise, spelled the way the mockup spells it. */
function transactionNote(changes: number): string {
  if (changes === 1) {
    return "Applied in one transaction — the one change or none.";
  }
  return `Applied in one transaction — all ${numberWord(changes)} changes or none.`;
}

/**
 * Why a file was turned away, in the shape the screen prints it. The server's
 * list arrives in `details.problems`; a file that never reached the server
 * fills the same list from the local read, so the operator sees one voice
 * either way.
 */
function problemsOf(err: unknown): string[] {
  const listed = errorProblems(err);
  if (listed.length > 0) return listed;
  return [
    err instanceof ApiError ? describeError(err.code) : describeError("internal"),
  ];
}

/**
 * The codes that judge the FILE — and the only ones allowed to turn it away.
 *
 * Everything else the plan endpoint can answer with — a 500, a proxy that gave
 * up, a connection that dropped — says nothing about the bytes: the same file
 * planned a moment ago, and would plan again. Sending those to state E would
 * print "this file can't be imported" over a fault on our side, and the only
 * ways out of that screen are Cancel and another file.
 */
const FILE_FAULT_CODES = new Set([
  "rewards_bad_json",
  "rewards_unknown_schema",
  "rewards_invalid",
  "rewards_too_many",
  "body_too_large",
]);

function isFileFault(err: unknown): boolean {
  return err instanceof ApiError && FILE_FAULT_CODES.has(err.code);
}

// ──────────────────────────────────────────────────────────────────────────────

interface Props {
  gameId: string;
  lbId: string;
  lb: Leaderboard;
  onClose: () => void;
  /** Handed the server's own counters — the tab owns the toast and the refetch. */
  onImported: (result: RewardsImportResult) => void;
}

export function ImportRewardsModal({
  gameId,
  lbId,
  lb,
  onClose,
  onImported,
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  /** Bumped per accepted file so a second pick can't be served the first one's plan. */
  const seq = useRef(0);

  const [picked, setPicked] = useState<File | null>(null);
  const [reading, setReading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [mode, setMode] = useState<RewardsImportMode>("merge");
  const [source, setSource] = useState<
    { seq: number; name: string; file: RewardsFile } | null
  >(null);
  /** Faults found before the server was ever asked: too big, unreadable, not this schema. */
  const [rejected, setRejected] = useState<{
    name: string;
    problems: string[];
  } | null>(null);

  const isTierBoard = lb.segmentation === "tier";
  const tiers: Tier[] = isTierBoard ? lb.tier_config?.tiers ?? [] : [];
  // A board with two reward tables needs a column saying which one each row
  // lands in: a reward is identified by (rank_basis + range), so two rows can
  // legitimately read `1 – 1` and be unrelated to each other.
  const showBasis = isMemberContribution(lb);

  // A POST behind useQuery: the endpoint writes nothing and is safe to call
  // again, and the mode belongs in the key — that is what makes switching it
  // a refetch rather than a piece of hand-rolled state.
  const planQ = useQuery({
    queryKey: ["leaderboard.rewards.plan", gameId, lbId, source?.seq ?? 0, mode],
    queryFn: () => planRewardsImport(gameId, lbId, { mode, ...source!.file }),
    enabled: source !== null,
    retry: false,
    gcTime: 0,
  });

  const applyMut = useMutation({
    mutationFn: () => importRewards(gameId, lbId, { mode, ...source!.file }),
    onSuccess: onImported,
  });

  // The first plan lands while the picker is still on screen; switching to the
  // wide dialog before it does would flash an empty 820px box. Derived during
  // render rather than in an effect, the way ImportReview derives its selection
  // from a fresh plan.
  const [planned, setPlanned] = useState(false);
  if (!planned && planQ.data !== undefined) setPlanned(true);

  // State E is about the FILE: it says this file can't be imported, and the way
  // out of it is another file. An apply that fails says nothing about the file
  // — the server planned it a moment ago — so it belongs on the plan screen,
  // where the plan is still on view and the button can simply be pressed again.
  // A plan that fails on anything but a file fault is the same story: it stays
  // on whichever screen the operator is standing on, with a way to ask again.
  const planError = source && planQ.isError ? planQ.error : null;
  const badFile = planError !== null && isFileFault(planError);
  const failure =
    rejected ??
    (source && badFile
      ? { name: source.name, problems: problemsOf(planError) }
      : null);

  function handleFile(file: File | null | undefined) {
    if (!file) return;
    setPicked(file);
    setSource(null);
    setRejected(null);
    setPlanned(false);
    // A new file is planned in the mode the dialog opens in, not the one the
    // last file happened to be left in — Replace is not something to inherit.
    setMode("merge");
    applyMut.reset();
  }

  function chooseAnother() {
    // Only opens the picker: cancelling the OS dialog must leave the plan the
    // operator is looking at exactly where it is.
    fileRef.current?.click();
  }

  /** Read the picked file, then let the query ask the server what it would do. */
  async function review() {
    if (!picked || reading) return;
    setReading(true);
    try {
      if (picked.size > MAX_REWARDS_FILE_BYTES) {
        setRejected({ name: picked.name, problems: [REWARDS_FILE_TOO_BIG] });
        return;
      }
      let text: string;
      try {
        text = await readRewardsFileText(picked);
      } catch {
        setRejected({
          name: picked.name,
          problems: ["Couldn't read this file. Try picking it again."],
        });
        return;
      }
      const parsed = parseRewardsFile(text);
      if (!parsed.ok) {
        setRejected({ name: picked.name, problems: parsed.problems });
        return;
      }
      seq.current += 1;
      setSource({ seq: seq.current, name: picked.name, file: parsed.file });
    } finally {
      setReading(false);
    }
  }

  const fileInput = (
    <input
      ref={fileRef}
      type="file"
      accept="application/json,.json"
      className={styles.fileInput}
      aria-label="Rewards file"
      onChange={(e) => {
        const file = e.target.files?.[0];
        // Reset so picking the same file twice fires change again.
        e.target.value = "";
        handleFile(file);
      }}
    />
  );

  // === E · the file is not one this board can take ==================== //
  if (failure) {
    return (
      <Modal
        open
        onClose={onClose}
        title="Import rewards"
        width="sm"
        footer={
          <>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="secondary" onClick={chooseAnother}>
              Choose another file
            </Button>
          </>
        }
      >
        <FileBar
          name={failure.name}
          file={source?.file}
          spaced
          onChooseAnother={chooseAnother}
        />
        <Alert
          variant="danger"
          title={`This file can’t be imported into ${lb.name}`}
        >
          <ul className={styles.problems}>
            {failure.problems.map((p) => (
              <li key={p}>{p}</li>
            ))}
          </ul>
        </Alert>
        {fileInput}
      </Modal>
    );
  }

  // === C / D · the plan =============================================== //
  if (source && planned) {
    const plan = planQ.data;
    const changes = plan
      ? plan.create.length + plan.update.length + plan.delete.length
      : 0;
    const deleting = (plan?.delete.length ?? 0) > 0;

    return (
      <Modal
        open
        onClose={onClose}
        title="Import rewards"
        width="lg"
        footer={
          <>
            {plan && changes > 0 && (
              <span className={styles.footerNote}>
                {transactionNote(changes)}
              </span>
            )}
            <Button
              variant="ghost"
              onClick={onClose}
              disabled={applyMut.isPending}
            >
              Cancel
            </Button>
            <Button
              /* Red exactly when the plan deletes something — which, in
                 Merge, it never does. */
              variant={deleting ? "danger" : "primary"}
              disabled={!plan || changes === 0}
              /* A plan that failed is not a plan still coming: spinning here
                 would promise an answer that is not on its way. */
              loading={applyMut.isPending || (!plan && planError === null)}
              onClick={() => applyMut.mutate()}
            >
              {applyLabel(mode, plan ? changes : null)}
            </Button>
          </>
        }
      >
        <FileBar
          name={source.name}
          file={source.file}
          onChooseAnother={chooseAnother}
        />

        <div className={styles.modeRow}>
          <div className={styles.seg} role="radiogroup" aria-label="Import mode">
            {(["merge", "replace"] as const).map((m) => (
              <button
                key={m}
                type="button"
                role="radio"
                aria-checked={mode === m}
                className={`${styles.segOpt} ${mode === m ? styles.segOptActive : ""}`}
                disabled={applyMut.isPending}
                onClick={() => {
                  // The failed apply described the other mode's plan; a fresh
                  // plan is on its way, so the message goes with the old one.
                  applyMut.reset();
                  setMode(m);
                }}
              >
                {MODE_LABEL[m]}
              </button>
            ))}
          </div>
          <p className={styles.modeHint}>{MODE_HINT[mode]}</p>
        </div>

        {applyMut.isError && (
          <div className={styles.alertSlot}>
            <Alert variant="danger" title="Nothing was imported">
              <ul className={styles.problems}>
                {problemsOf(applyMut.error).map((p) => (
                  <li key={p}>{p}</li>
                ))}
              </ul>
              The import is one transaction, so the board is exactly as it was.
              Trying again is safe: the server works the plan out afresh from
              the file, including a board that moved in the meantime.
            </Alert>
          </div>
        )}

        {planError !== null ? (
          // Nothing of the previous mode survives here — the plan is keyed by
          // mode, so there is no table and no count left to read under a
          // switch that already points at the new one.
          <div className={styles.planFailed}>
            <PlanFailedAlert error={planError} />
            <div className={styles.retryRow}>
              <Button
                variant="secondary"
                loading={planQ.isFetching}
                onClick={() => void planQ.refetch()}
              >
                Try again
              </Button>
            </div>
          </div>
        ) : !plan ? (
          <div className={styles.planLoading}>
            <Skeleton height={180} />
          </div>
        ) : (
          <>
            {mode === "replace" && plan.delete.length > 0 && (
              <div className={styles.alertSlot}>
                <Alert
                  variant="warn"
                  title={`${plan.delete.length} ${plural(plan.delete.length, "reward", "rewards")} will be deleted`}
                >
                  Deleting a reward does not touch grants already handed out —
                  they carry their own payload snapshot — but the reward itself
                  cannot be restored.
                </Alert>
              </div>
            )}

            <div className={styles.summary}>
              <span className={styles.summaryLabel}>Plan</span>
              {summaryBadges(plan).map((b) => (
                <Badge key={b.text} variant={b.variant}>
                  {b.text}
                </Badge>
              ))}
            </div>

            <div className={styles.planWrap}>
              <table className={styles.plan}>
                <thead>
                  <tr>
                    <th style={{ width: 82 }}>Change</th>
                    <th style={{ width: 44 }}>Pos.</th>
                    <th style={{ width: 150 }}>Name</th>
                    {isTierBoard && <th style={{ width: 96 }}>Tier</th>}
                    {showBasis && <th style={{ width: 110 }}>Basis</th>}
                    <th style={{ width: 100 }}>Type</th>
                    <th style={{ width: 82 }}>Range</th>
                    <th>Payload</th>
                  </tr>
                </thead>
                <tbody>
                  {planRows(plan).map((row, i) => (
                    <PlanTableRow
                      key={`${row.kind}-${i}`}
                      row={row}
                      tiers={tiers}
                      showTier={isTierBoard}
                      showBasis={showBasis}
                    />
                  ))}
                </tbody>
              </table>

              {mode === "merge" && plan.kept.length > 0 && (
                <div className={styles.collapse}>
                  <Info size={13} aria-hidden="true" />
                  <KeptNote kept={plan.kept} />
                </div>
              )}
            </div>
          </>
        )}
        {fileInput}
      </Modal>
    );
  }

  // === B · nothing picked yet ========================================= //
  return (
    <Modal
      open
      onClose={onClose}
      title="Import rewards"
      width="sm"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!picked}
            loading={reading || planQ.isFetching}
            onClick={review}
          >
            Review changes
          </Button>
        </>
      }
    >
      {planError !== null && (
        // The first plan never arrived. The file is still picked and still
        // named below, so "Review changes" is the second go — no new pick.
        <div className={styles.retrySlot}>
          <PlanFailedAlert error={planError} />
        </div>
      )}
      <div
        className={
          dragOver ? `${styles.dropzone} ${styles.dropzoneOver}` : styles.dropzone
        }
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFile(e.dataTransfer?.files?.[0]);
        }}
      >
        <span className={styles.dzIcon} aria-hidden="true">
          <Upload size={20} />
        </span>
        <span className={styles.dzTitle}>
          {picked ? picked.name : "Drop a rewards file here"}
        </span>
        <span className={styles.dzOr}>or</span>
        <Button variant="secondary" size="sm" onClick={chooseAnother}>
          Choose file
        </Button>
        <span className={styles.dzHint}>.json · up to 1 MB</span>
      </div>
      <p className={styles.dzHelp}>
        The file is the one <b>Export JSON</b> produces on any leaderboard —{" "}
        <code>social-hub/leaderboard-rewards@1</code>. Nothing is written until
        you review the changes on the next step.
      </p>
      {fileInput}
    </Modal>
  );
}

// ──────────────────────────────────────────────────────────────────────────────
// Pieces
// ──────────────────────────────────────────────────────────────────────────────

/**
 * A plan that never arrived, for a reason the file is not answerable for. Same
 * shape as the failed-apply alert one screen over: what went wrong, then why a
 * second go costs nothing.
 */
function PlanFailedAlert({ error }: { error: unknown }) {
  return (
    <Alert variant="danger" title="Couldn't work out the plan">
      <ul className={styles.problems}>
        {problemsOf(error).map((p) => (
          <li key={p}>{p}</li>
        ))}
      </ul>
      Planning only asks what an import would do, so nothing was written and the
      file is untouched. Trying again is safe.
    </Alert>
  );
}

/**
 * The chosen file, with the provenance the envelope carries. `file` is absent
 * when the read itself failed — the name is then all that is true about it,
 * and inventing a meta line would be worse than leaving it off.
 */
function FileBar({
  name,
  file,
  spaced,
  onChooseAnother,
}: {
  name: string;
  file?: RewardsFile;
  spaced?: boolean;
  onChooseAnother: () => void;
}) {
  const meta = file ? rewardsFileMeta(file) : null;
  return (
    <div
      className={`${styles.fileBar} ${spaced ? styles.fileBarSpaced : ""}`}
    >
      <span className={styles.fileIcon} aria-hidden="true">
        <FileIcon size={16} />
      </span>
      <div className={styles.fileCol}>
        <div className={styles.fileName}>{name}</div>
        {meta && (
          <div className={styles.fileMeta}>
            {meta.count}
            {meta.boardName && (
              <>
                {" · from "}
                <b>{meta.boardName}</b>
              </>
            )}
            {meta.projectName && ` · ${meta.projectName}`}
            {meta.exported && ` · ${meta.exported}`}
          </div>
        )}
      </div>
      <Button variant="ghost" size="sm" onClick={onChooseAnother}>
        Choose another…
      </Button>
    </div>
  );
}

function PlanTableRow({
  row,
  tiers,
  showTier,
  showBasis,
}: {
  row: PlanRow;
  tiers: Tier[];
  showTier: boolean;
  showBasis: boolean;
}) {
  const badge = ROW_BADGE[row.kind];
  const kind = fileRewardKind(row.entry);
  const payload = rewardPayloadText(row.entry.payload);
  const diff = row.before ? diffItems(row.before, row.entry) : [];
  const rowClass =
    row.kind === "delete"
      ? styles.rowDelete
      : row.kind === "unchanged"
        ? styles.rowUnchanged
        : undefined;
  return (
    <>
      <tr className={rowClass}>
        <td>
          <Badge variant={badge.variant}>{badge.label}</Badge>
        </td>
        <td className={styles.monoCell}>{row.entry.position}</td>
        <td>
          <b>{row.entry.name}</b>
        </td>
        {showTier && (
          <td>
            <PlanTierChip tierKey={row.entry.tier_key} tiers={tiers} />
          </td>
        )}
        {showBasis && (
          <td>
            {/* The value verbatim, not the table's own «basis: guild» wording:
                this column is about what the FILE says, and the file's word is
                the one an operator would go and edit. */}
            <span className={styles.monoCell}>{fileRewardBasis(row.entry)}</span>
          </td>
        )}
        <td>
          <span className={`${styles.typePill} ${typeClass(kind)}`}>{kind}</span>
        </td>
        <td className={styles.monoCell}>{fileRewardRangeText(row.entry)}</td>
        <td>
          <span className={styles.payloadCell} title={payload}>
            {payload}
          </span>
        </td>
      </tr>
      {diff.length > 0 && (
        <tr>
          <td />
          <td
            className={styles.diffCell}
            colSpan={5 + (showTier ? 1 : 0) + (showBasis ? 1 : 0)}
          >
            <div className={styles.diff}>
              {diff.map((d) => (
                <span className={styles.diffItem} key={d.key}>
                  <span className={styles.diffKey}>{d.key}</span>
                  <code className={styles.was}>{d.was}</code>
                  <span className={styles.arrow} aria-hidden="true">
                    →
                  </span>
                  <code className={styles.now}>{d.now}</code>
                </span>
              ))}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

function typeClass(kind: ReturnType<typeof fileRewardKind>): string {
  if (kind === "tier_threshold") return styles.typeAccent;
  if (kind === "percentile") return styles.typeWarn;
  return styles.typeInfo;
}

/**
 * Same three shapes as the table's Tier cell. A key the config no longer has
 * only reaches here on a delete row — the server refuses to import one — so it
 * is shown raw rather than dressed as an error the import would fix.
 */
function PlanTierChip({
  tierKey,
  tiers,
}: {
  tierKey?: string | null;
  tiers: Tier[];
}) {
  if (!tierKey) {
    return (
      <span
        className={`${styles.tierChip} ${styles.tierChipAll}`}
        title="No tier_key — this reward is granted inside every tier"
      >
        All tiers
      </span>
    );
  }
  const tier = tiers.find((t) => t.key === tierKey);
  return (
    <span className={styles.tierChip} title={`tier_key: ${tierKey}`}>
      {tier ? tier.name.trim() || tier.key : tierKey}
    </span>
  );
}

/**
 * How many of the untouched rewards the footnote names before it counts the
 * rest. The mockup draws that footnote as one 7px-tall strip (`.collapse`,
 * L703) and nothing in the layout wraps it — a board of thirty rewards met by
 * a file of two would grow it into a paragraph of names. Three is enough to
 * recognise which corner of the table is being left alone.
 */
const KEPT_NAMES_SHOWN = 3;

/** Merge's one-line footnote: the board is wider than the file, and stays so. */
function KeptNote({ kept }: { kept: RewardsFileEntry[] }) {
  if (kept.length === 1) {
    return (
      <span>
        <b>1 reward</b> on this board is not in the file — <b>{kept[0].name}</b>{" "}
        stays as it is.
      </span>
    );
  }
  const shown = kept.slice(0, KEPT_NAMES_SHOWN);
  const rest = kept.length - shown.length;
  return (
    <span>
      <b>{kept.length} rewards</b> on this board are not in the file —{" "}
      <b>{shown.map((k) => k.name).join(", ")}</b>
      {rest > 0 ? ` and ${rest} more` : ""}{" "}
      stay as they are.
    </span>
  );
}
